import type { ApprovalMode, ClFarmAlmType, EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address, PublicClient } from "viem"
import type { Config } from "wagmi"
import { ClFarmStore } from "@/domains/cl-farm/cl-farm-store"
import { approveOwnerSpenderAllowance, redeemClFarmLiquidityFromVault } from "@/domains/on-chain"
import { erc20Abi } from "viem"

type RedeemClFarmLiquidityActionInteraction = {
  connectedAddress: Address
  amount: bigint
  approvalMode: ApprovalMode
  chainId: number
  clFarm: ClFarmStore | null | undefined
  config: Config
  emitter: EventEmitter<EmitEvent>
  publicClient: PublicClient
  onSuccess: () => void
}

/**
 * This is the equivalent to the CL Farms "redeem"
 */
export const redeemClFarmLiquidityFromVaultInteraction = async ({
  connectedAddress,
  amount,
  approvalMode,
  chainId,
  clFarm,
  config,
  emitter,
  publicClient,
  onSuccess,
}: RedeemClFarmLiquidityActionInteraction) => {
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }

  if (!clFarm) return
  const farmAddress = clFarm?.farmAddress?.toViemAddress()
  const providerLpTokenAddress = clFarm.providerLp.address.toViemAddress()
  const vaultAddress = clFarm.vaultAddress?.toViemAddress()

  if (!farmAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm farm address is invalid. Please try again.",
      variant: "error",
    })
  }
  if (!providerLpTokenAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm ichi token address is invalid. Please try again.",
      variant: "error",
    })
  }
  if (!vaultAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm vault address is invalid. Please try again.",
      variant: "error",
    })
  }

  //# STEP ONE: Check if the user has enough balance in their wallet to withdraw
  if (clFarm.onChainUserBalance < amount) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${clFarm.displayName} in your wallet. You are trying to withdraw ${clFarm.formatUnits(amount - clFarm.onChainUserBalance)} more than available.`,
      variant: "error",
    })
    return
  }

  // STEP TWO - Does the user need to provide an allowance?
  const allowance = await publicClient.readContract({
    address: providerLpTokenAddress,
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, vaultAddress],
  })

  if (allowance < amount) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${clFarm.formatUnits(amount)} ${clFarm.displayName}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: providerLpTokenAddress,
      spenderAddress: vaultAddress,
      spenderAmount: amount,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You may now proceed to redeem",
        variant: "success",
      })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error ?? "unknown error",
        variant: "error",
      })
      return
    }
  }
  //#endregion

  // STEP THREE: Withdraw the CL Farm Liquidity from the Farm
  const redeemClFarmLiquidityResult = await redeemClFarmLiquidityFromVault({
    account: connectedAddress,
    chainId,
    config,
    vaultAddress,
    amount: amount,
    functionName: "withdraw",
    alm: clFarm.alm as ClFarmAlmType,
  })

  if (redeemClFarmLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-withdraw-liquidity", {
      title: `Withdraw  ${clFarm.displayName}`,
      description: `Withdrew ${clFarm.formatUnits(amount)} ${clFarm.displayName} tokens`,
      variant: "success",
    })
  } else {
    emitter.emit("error-withdraw-liquidity", {
      title: `Unable to withdraw ${clFarm.formatUnits(amount)} from ${clFarm.displayName}`,
      description: redeemClFarmLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }

  return redeemClFarmLiquidityResult
}
